/**
 * useCurrencyDragAndDrop Hook
 * 
 * Manages drag and drop functionality for currency ordering.
 * 
 * Note: This file must be .tsx (not .ts) because it contains JSX syntax.
 */

import React from "react";
import { useSensors, useSensor, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../../../firebase";
import { Currency } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

interface UseCurrencyDragAndDropParams {
  workspaceId: string;
  currencies: Currency[];
  setCurrencies: React.Dispatch<React.SetStateAction<Currency[]>>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UseCurrencyDragAndDropReturn {
  sensors: ReturnType<typeof useSensors>;
  handleCurrencyDragEnd: (event: DragEndEvent) => Promise<void>;
}

/**
 * Hook to manage drag and drop for currency ordering
 */
export function useCurrencyDragAndDrop({
  workspaceId,
  currencies,
  setCurrencies,
  setSnackbarElement,
  setSnackbarOpen,
}: UseCurrencyDragAndDropParams): UseCurrencyDragAndDropReturn {
  // Drag and drop sensors for currency ordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle currency drag end for ordering
  const handleCurrencyDragEnd = async (event: DragEndEvent): Promise<void> => {
    // In demo mode, currency order cannot be changed
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Currency order cannot be changed. Please log in to reorder currencies." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser) {
      console.error("Cannot save currency order: missing workspaceId or user not authenticated");
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = currencies.findIndex((c) => c.code === active.id);
    const newIndex = currencies.findIndex((c) => c.code === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder currencies
    const reorderedCurrencies = arrayMove(currencies, oldIndex, newIndex);
    
    // Create new order based on reordered currencies (exact order as displayed)
    const newOrder = reorderedCurrencies.map((c) => c.code);
    
    // Update local state immediately for better UX
    setCurrencies(reorderedCurrencies);
    
    try {
      // Save new order directly to settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      
      // Use setDoc with merge to ensure the document exists and update currencyOrder
      await setDoc(settingsRef, { currencyOrder: newOrder }, { merge: true });
      
      console.log("✅ Currency order saved:", newOrder);
    } catch (error: any) {
      console.error("Error updating currency order:", error);
      console.error("Error details:", error.message, error.code);
      // Revert on error
      setCurrencies(currencies);
      alert(`Failed to update currency order: ${error.message || "Unknown error"}`);
    }
  };

  return {
    sensors,
    handleCurrencyDragEnd,
  };
}
